import {LayerData} from "@app/common/components/viewers/configurator/extensions/bd-cloud/types"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import type {ParametersParameters} from "@cm/template-nodes"
import {mattressIoMetaDataSchema, panelPathName, stackPathName} from "@cm/mattress-nodes"
import {remapLayerParameters} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/layers"
import {LayerStack, type StackLayer} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/layer-stack"
import {getParameterStorage} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/parameter-storage"
import {removeUnreferencedQuiltSetups} from "@app/common/components/viewers/configurator/extensions/bd-cloud/helpers/quilt-setup"

/* Determines what the ui needs, currently a single implementation exists (RabricStackLayerModel) */
export interface LayerModel {
    getLayers(): LayerData[]
    addLayer(displayIndex: number): void
    removeLayer(displayIndex: number): void
    canMove(sourceDisplayIndex: number, targetDisplayIndex: number): boolean
    moveLayer(sourceDisplayIndex: number, targetDisplayIndex: number): void
}

export const STACK_UNIT_ID_PREFIX = "layer"
const STACK_PREFIX = "fabric-stack"
const LAYER_COUNT_ID = "layer-count"
const LAYER_PREFIX_REGEX = new RegExp(`^${STACK_UNIT_ID_PREFIX}-(\\d+)$`)

type LayerInfo = {
    name: string
    stackId?: string
    layerId?: number
}

type ParsedLayer = {
    name: string
    rank: number
    inputParameterIds: string[]
    stackId?: string
    layerId?: number
}

/* Meta data is static, the layer data inside the stack cannot be derived from it. Rules:
- Meta data: top panel, rank 0; stack, rank 1; bottom panel, rank 2
- When inside a stack, derive layer data from the layer instance id (part of the descriptor id, /layer-n/)*/
function extractLayerInfo(pathName: string, descriptorId: string): LayerInfo | undefined {
    if (pathName !== stackPathName) return {name: pathName}
    const stackIndex = descriptorId.indexOf(STACK_PREFIX)
    if (stackIndex === -1) return undefined
    const afterStack = descriptorId.substring(stackIndex + STACK_PREFIX.length + 1)
    const layerName = afterStack.split("/")[0]
    const match = layerName.match(LAYER_PREFIX_REGEX)
    if (!match) return undefined
    return {name: layerName, stackId: stackPathName, layerId: parseInt(match[1], 10)}
}

/* Represents top panel, single stack, bottom panel*/
export class FabricStackLayerModel implements LayerModel {
    private readonly parsedLayers: ParsedLayer[]
    private readonly stack: LayerStack
    private readonly context: ExtensionContext

    constructor(context: ExtensionContext) {
        this.context = context
        this.parsedLayers = this.parseLayersFromDescriptors()
        this.stack = this.buildStack()
    }

    getLayers(): LayerData[] {
        const fixedLayers: (LayerData & {rank: number})[] = this.parsedLayers
            .filter((l) => l.stackId === undefined)
            .map((l) => ({
                name: l.name,
                parameterIds: l.inputParameterIds,
                stackId: undefined,
                layerId: undefined,
                canRemove: false,
                canAdd: l.rank === 0,
                rank: l.rank,
            }))

        const stackRank = this.parsedLayers.find((l) => l.stackId !== undefined)?.rank ?? 0
        const displayStackLayers = this.stack.getDisplayLayers().map((l) => ({
            name: l.name,
            parameterIds: l.inputParameterIds,
            stackId: l.stackId,
            layerId: l.layerId,
            canRemove: true,
            canAdd: true,
            rank: stackRank,
        }))

        const allLayers = [...fixedLayers, ...displayStackLayers]
        allLayers.sort((a, b) => a.rank - b.rank)

        return allLayers.map(({rank: _rank, ...layer}) => layer)
    }

    /** Counts stack layers before a position in the flat display list to derive the stack-relative index. */
    private flatToStackIndex(layers: LayerData[], flatIndex: number): number {
        return layers.slice(0, flatIndex).filter((l) => l.stackId !== undefined).length
    }

    /* displayIndex is an index in the flat list of all layers, returned by getLayers. */
    addLayer(displayIndex: number): void {
        const layers = this.getLayers()
        const stackInsertIndex = this.flatToStackIndex(layers, displayIndex)
        const isAdjacentToStack = layers[displayIndex - 1]?.stackId !== undefined || layers[displayIndex]?.stackId !== undefined || this.stack.layerCount === 0
        if (!isAdjacentToStack) throw new Error(`Cannot add layer at index ${displayIndex}: not adjacent to a stack`)

        const {mapping, newCount} = this.stack.buildAddMapping(stackInsertIndex)
        this.applyLayerRemapping(mapping, newCount)
    }

    removeLayer(displayIndex: number): void {
        const layers = this.getLayers()
        const layer = layers[displayIndex]
        if (!layer) throw new Error(`Layer at index ${displayIndex} not found`)
        if (layer.stackId === undefined) throw new Error(`Layer "${layer.name}" is not a stack layer and cannot be removed`)

        const stackDisplayIndex = this.flatToStackIndex(layers, displayIndex)

        const {mapping, newCount} = this.stack.buildRemoveMapping(stackDisplayIndex)
        this.applyLayerRemapping(mapping, newCount)
    }

    canMove(sourceDisplayIndex: number, targetDisplayIndex: number): boolean {
        const layers = this.getLayers()
        const sourceLayer = layers[sourceDisplayIndex]
        const targetLayer = layers[targetDisplayIndex]
        if (!sourceLayer || !targetLayer) return false
        if (sourceLayer.stackId === undefined || targetLayer.stackId === undefined) return false

        const stackSource = this.flatToStackIndex(layers, sourceDisplayIndex)
        const stackTarget = this.flatToStackIndex(layers, targetDisplayIndex)

        return this.stack.canMove(stackSource, stackTarget)
    }

    moveLayer(sourceDisplayIndex: number, targetDisplayIndex: number): void {
        const layers = this.getLayers()
        const sourceLayer = layers[sourceDisplayIndex]
        const targetLayer = layers[targetDisplayIndex]
        if (!sourceLayer || !targetLayer) throw new Error(`Invalid move indices: ${sourceDisplayIndex} → ${targetDisplayIndex}`)
        if (sourceLayer.stackId === undefined || targetLayer.stackId === undefined) throw new Error("Cannot move non-stack layers")

        const stackSource = this.flatToStackIndex(layers, sourceDisplayIndex)
        const stackTarget = this.flatToStackIndex(layers, targetDisplayIndex)

        const {mapping, newCount} = this.stack.buildMoveMapping(stackSource, stackTarget)
        this.applyLayerRemapping(mapping, newCount)
    }

    private parseLayersFromDescriptors(): ParsedLayer[] {
        const descriptors = this.context.sceneManagerService.$descriptorList()
        const layerMap = new Map<string, ParsedLayer>()

        for (const d of descriptors) {
            const parsed = mattressIoMetaDataSchema.safeParse(d.props.metaData)
            if (!parsed.success) continue

            const panelPath = parsed.data.uiPaths.find((p) => p.name === panelPathName)
            if (!panelPath?.path) continue

            const layerInfo = extractLayerInfo(panelPath.path.name, d.props.id)
            if (!layerInfo) continue
            const existing = layerMap.get(layerInfo.name)
            if (existing) {
                existing.inputParameterIds.push(d.props.id)
            } else {
                layerMap.set(layerInfo.name, {
                    name: layerInfo.name,
                    rank: panelPath.path.rank,
                    inputParameterIds: [d.props.id],
                    stackId: layerInfo.stackId,
                    layerId: layerInfo.layerId,
                })
            }
        }

        return Array.from(layerMap.values())
    }

    private buildStack(): LayerStack {
        const stackInputs: StackLayer[] = this.parsedLayers
            .filter((l): l is ParsedLayer & {stackId: string; layerId: number} => l.stackId !== undefined && l.layerId !== undefined)
            .map((l) => ({name: l.name, inputParameterIds: l.inputParameterIds, stackId: l.stackId, layerId: l.layerId}))
        return new LayerStack(stackInputs, true)
    }

    private getLayerCountDescriptorId(): string {
        const descriptors = this.context.sceneManagerService.$descriptorList()
        const d = descriptors.find((d) => d.props.id.endsWith("/" + LAYER_COUNT_ID))
        if (!d) throw new Error("Layer count descriptor not found")
        return d.props.id
    }

    private applyLayerRemapping(mapping: Map<number, number>, newLayerCount: number): void {
        const layerCountDescriptorId = this.getLayerCountDescriptorId()

        this.context.sceneManagerService.modifyTemplateGraph((templateGraph) => {
            const {storingInstance, key} = getParameterStorage(templateGraph, layerCountDescriptorId)
            const parametersNode = storingInstance.parameters.parameters
            const currentParams = parametersNode.parameters

            //Remark: The stack params include accessory parameters, so the accessories also move with the layer. For the current mattress setup, the layer top curve hosts an accessory stack.
            const stackParams: ParametersParameters = {}
            const nonStackParams: ParametersParameters = {}

            for (const [key, value] of Object.entries(currentParams)) {
                if (key.includes(STACK_PREFIX)) {
                    stackParams[key] = value
                } else {
                    nonStackParams[key] = value
                }
            }

            const remappedStackParams = remapLayerParameters(stackParams, STACK_UNIT_ID_PREFIX, mapping)

            parametersNode.replaceParameters({
                ...nonStackParams,
                ...remappedStackParams,
                [key]: newLayerCount,
            })

            //A removed layer may have carried a quilt, its setup template instance would otherwise stay behind
            removeUnreferencedQuiltSetups(templateGraph)
        })
    }
}
